import type { Notification } from '@nexo/types';
import { Avatar, AvatarStack, IconButton, Popover } from '@nexo/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  CalendarPlus,
  Ellipsis,
  FolderOpen,
  Hash,
  ListTodo,
  Lock,
  Pin,
  Settings,
  UserPlus,
  Users,
  Webhook,
} from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router';
import { lastChannelKey } from '../../layouts/AppTopBar';
import { TeamHeader } from '../../layouts/TeamHeader';
import { useTeam, type RightPanel } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { useDocumentVisible } from '../../lib/visibility';
import { keys } from '../../lib/queries';
import { useRealtime } from '../../realtime/RealtimeProvider';
import { CallControls } from '../calls/CallControls';
import { VoiceChannelView } from '../calls/VoiceChannelView';
import { ScheduleMeetingDialog } from '../meetings/ScheduleMeetingDialog';
import { ChannelNotifyMenu } from '../notifications/ChannelNotifyMenu';
import { channelLabel } from '../channels/ChannelSidebar';
import { Composer, type ComposerHandle } from './Composer';
import { MessageList } from './MessageList';
import { TypingIndicator } from './TypingIndicator';
import { can, canIn } from '../../lib/permissions';
import { ChannelSettingsDialog } from '../channels/ChannelSettings';
import { jumpToMessage } from './replying';
import { useTranslation } from 'react-i18next';

export function ChannelView() {
  const { channelId = '' } = useParams();
  const { team, channels, members, online, setRightPanel, openDialog, openThread } = useTeam();
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link from a thread notification: ?thread=<messageId> opens that thread.
  const threadParam = searchParams.get('thread');
  useEffect(() => {
    if (!threadParam) return;
    openThread(threadParam);
    setSearchParams(
      (params) => {
        params.delete('thread');
        return params;
      },
      { replace: true },
    );
  }, [threadParam, openThread, setSearchParams]);

  // Link to a message (?m=<messageId>, from «Copiar enlace»): scroll to it once it has loaded.
  const messageParam = searchParams.get('m');
  useEffect(() => {
    if (!messageParam) return;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (jumpToMessage(messageParam) || tries > 20) {
        clearInterval(timer);
        setSearchParams(
          (params) => {
            params.delete('m');
            return params;
          },
          { replace: true },
        );
      }
    }, 250);
    return () => clearInterval(timer);
  }, [messageParam, setSearchParams]);
  const { activeChannelRef } = useRealtime();
  const queryClient = useQueryClient();
  const channel = channels.find((c) => c.id === channelId);
  const composer = useRef<ComposerHandle>(null);
  const [dragging, setDragging] = useState(false);
  const markedChannel = useRef<string | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { t } = useTranslation();

  // Remember it for the "Chat" tab and tell realtime what is on screen.
  useEffect(() => {
    if (!channel) return;
    activeChannelRef.current = channel.id;
    localStorage.setItem(lastChannelKey(team.id), channel.id);
    return () => {
      activeChannelRef.current = null;
    };
  }, [channel?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The conversation on screen never shows as unread: whenever it has unread messages (opened
  // it, came back to the window, or a refetch/poll brought new ones) mark them as read, along
  // with its pending mention notifications.
  const visible = useDocumentVisible();
  const unread = channel?.unreadCount ?? 0;
  useEffect(() => {
    if (!channel || !visible) return;
    const pending = (queryClient.getQueryData<Notification[]>(keys.notifications) ?? []).filter(
      (n) => !n.readAt && n.data.type === 'mention' && n.data.channelId === channel.id,
    );
    if (pending.length > 0) {
      const now = new Date().toISOString();
      queryClient.setQueryData<Notification[]>(keys.notifications, (list) =>
        list?.map((n) => (pending.includes(n) ? { ...n, readAt: now } : n)),
      );
      for (const n of pending) void api.notifications.markRead(n.id);
    }
    if (unread === 0 && pending.length === 0 && markedChannel.current === channel.id) return;
    markedChannel.current = channel.id;
    cache.patchChannel(queryClient, team.id, channel.id, () => ({
      unreadCount: 0,
      mentionCount: 0,
    }));
    void api.channels.markRead(channel.id);
  }, [channel?.id, unread, visible]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!channel) {
    const general = channels.find((c) => c.kind === 'channel' && c.mode === 'text');
    return (
      <Navigate
        to={general ? `/app/t/${team.id}/c/${general.id}` : `/app/t/${team.id}/tasks`}
        replace
      />
    );
  }

  if (channel.mode === 'voice') return <VoiceChannelView channel={channel} />;

  const peer = channel.kind === 'dm' ? channel.dmPeer : null;
  const peerOnline = peer && online ? online.has(peer.id) : undefined;
  const subtitle = peer
    ? peerOnline === undefined
      ? (peer.title ?? t('Mensaje directo'))
      : peerOnline
        ? t('En línea')
        : t('Desconectado')
    : channel.description;

  const menu: { icon: ReactNode; label: string; panel: RightPanel }[] = [
    { icon: <Users />, label: 'Miembros', panel: 'members' },
    { icon: <ListTodo />, label: 'Tareas de la conversación', panel: 'tasks' },
    { icon: <Pin />, label: 'Mensajes fijados', panel: 'pins' },
    { icon: <FolderOpen />, label: 'Archivos compartidos', panel: 'files' },
    ...(peer
      ? []
      : [{ icon: <Webhook />, label: 'Integraciones (webhooks)', panel: 'integrations' as const }]),
  ];

  return (
    <>
      <TeamHeader
        actions={
          <div className="flex items-center gap-1">
            <CallControls channel={channel} />
            {!peer && (
              <button
                type="button"
                onClick={() => setRightPanel('members')}
                className="mr-1 rounded-full max-sm:hidden"
                title={t('Ver miembros')}
              >
                <AvatarStack people={members} max={4} size="sm" />
              </button>
            )}
            {!peer && can(team, 'invite_members') && (
              <IconButton label={t('Invitar al equipo')} onClick={() => openDialog('members')}>
                <UserPlus />
              </IconButton>
            )}
            <Popover
              className="w-72 p-1"
              trigger={({ toggle, open }) => (
                <IconButton label={t('Más opciones')} active={open} onClick={toggle}>
                  <Ellipsis />
                </IconButton>
              )}
            >
              {({ close }) => (
                <ul role="menu">
                  {menu.map((item) => (
                    <li key={item.panel}>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          close();
                          setRightPanel(item.panel);
                        }}
                        className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-surface-2 [&_svg]:size-4 [&_svg]:text-muted"
                      >
                        {item.icon}
                        {t(item.label)}
                      </button>
                    </li>
                  ))}
                  <li>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        close();
                        setScheduling(true);
                      }}
                      className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-surface-2 [&_svg]:size-4 [&_svg]:text-muted"
                    >
                      <CalendarPlus />
                      {t('Programar reunión')}
                    </button>
                  </li>
                  {!peer && canIn(channel, 'manage_channels') && (
                    <li>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          close();
                          setSettingsOpen(true);
                        }}
                        className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-surface-2 [&_svg]:size-4 [&_svg]:text-muted"
                      >
                        <Settings />
                        {t('Ajustes del canal')}
                      </button>
                    </li>
                  )}
                  <li className="mt-1 border-t border-border">
                    <ChannelNotifyMenu channel={channel} onDone={close} />
                  </li>
                </ul>
              )}
            </Popover>
            <ScheduleMeetingDialog
              open={scheduling}
              onClose={() => setScheduling(false)}
              channelId={channel.id}
            />
            {settingsOpen && (
              <ChannelSettingsDialog
                channel={channel}
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
              />
            )}
          </div>
        }
      >
        {peer ? (
          <Avatar
            id={peer.id}
            name={peer.displayName}
            src={peer.avatarUrl}
            size="sm"
            online={peerOnline}
          />
        ) : channel.isPrivate ? (
          <Lock className="size-4 shrink-0 text-muted" />
        ) : (
          <Hash className="size-[18px] shrink-0 text-muted" />
        )}
        <h1 className="shrink-0 truncate text-[17px] font-semibold tracking-tight">
          {channelLabel(channel)}
        </h1>
        {subtitle && (
          <p className="min-w-0 truncate text-[13px] text-muted max-sm:hidden">{subtitle}</p>
        )}
      </TeamHeader>
      <div
        className="relative flex min-h-0 flex-1 flex-col"
        onDragOver={(e: DragEvent) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e: DragEvent) => {
          if (e.currentTarget === e.target) setDragging(false);
        }}
        onDrop={(e: DragEvent) => {
          if (!e.dataTransfer.files.length) return;
          e.preventDefault();
          setDragging(false);
          composer.current?.addFiles(Array.from(e.dataTransfer.files));
        }}
      >
        <MessageList key={channel.id} channel={channel} />
        <TypingIndicator channelId={channel.id} />
        <Composer key={`composer-${channel.id}`} ref={composer} channel={channel} />
        {dragging && (
          <div className="pointer-events-none absolute inset-3 z-20 flex items-center justify-center rounded-2xl border-2 border-dashed border-accent bg-accent-soft/80 backdrop-blur-sm">
            <p className="text-base font-semibold text-accent">
              {t('Suelta para adjuntar a {{name}}', { name: channelLabel(channel) })}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
